// Factory Directory — READ-ONLY data access for internal_directory_* tables.
// Do NOT use trading public.factories. No create/update/delete.

import { supabase } from '../lib/supabase';

// ─── Constants ───────────────────────────────────────────────────────────────

export const OPERATIONAL_STATUS_OPTIONS = [
  'Working',
  'Closed',
  'Proposed',
  'Khandsari/Jaggery',
  'Unknown',
];

export const FACTORY_NATURE_OPTIONS = [
  'Private',
  'Co-operative',
  'Public Limited',
  'Public Sector',
  'Other',
  'Unknown',
];

export const DEFAULT_PAGE_SIZE = 50;
export const MAX_PAGE_SIZE = 100;

const FACTORY_LIST_COLUMNS = [
  'id',
  'factory_name',
  'plant_name',
  'state',
  'district',
  'operational_status',
  'factory_nature',
  'mill_code',
  'source_pincode',
  'trusted_pincode',
  'city',
  'address',
].join(',');

const FACTORY_DETAIL_COLUMNS = [
  'id',
  'factory_name',
  'plant_name',
  'state',
  'district',
  'address',
  'city',
  'source_pincode',
  'trusted_pincode',
  'mill_code',
  'phones',
  'emails',
  'website',
  'registered_office',
  'operational_status',
  'factory_nature',
  'source_factory_name',
].join(',');

// ─── Helpers ─────────────────────────────────────────────────────────────────

const isBlank = (v) => v === null || v === undefined || String(v).trim() === '';

const cleanText = (v) => {
  if (isBlank(v)) return null;
  const s = String(v).trim();
  if (!s || s === 'N.A.' || s === 'n.a.' || s === 'NULL' || s === 'null' || s === 'undefined') return null;
  return s;
};

const asStringArray = (value) => {
  if (value == null) return [];
  if (Array.isArray(value)) {
    return value.map(cleanText).filter(Boolean);
  }
  if (typeof value === 'string') {
    const t = cleanText(value);
    return t ? [t] : [];
  }
  return [];
};

const normalizeWebsite = (url) => {
  const t = cleanText(url);
  if (!t) return null;
  if (/^https?:\/\//i.test(t)) return t;
  return `https://${t}`;
};

const mapFactoryListRow = (row) => {
  const managerCountRaw = row?.internal_directory_managers;
  let managerCount = 0;
  if (Array.isArray(managerCountRaw) && managerCountRaw.length > 0) {
    const c = managerCountRaw[0]?.count;
    managerCount = typeof c === 'number' ? c : parseInt(c, 10) || 0;
  }

  return {
    id: row?.id,
    factoryName: cleanText(row?.factory_name),
    plantName: cleanText(row?.plant_name),
    state: cleanText(row?.state),
    district: cleanText(row?.district),
    operationalStatus: cleanText(row?.operational_status),
    factoryNature: cleanText(row?.factory_nature),
    millCode: cleanText(row?.mill_code),
    sourcePincode: cleanText(row?.source_pincode),
    trustedPincode: cleanText(row?.trusted_pincode),
    city: cleanText(row?.city),
    address: cleanText(row?.address),
    managerCount,
  };
};

const mapFactoryDetail = (row) => ({
  id: row?.id,
  factoryName: cleanText(row?.factory_name),
  plantName: cleanText(row?.plant_name),
  state: cleanText(row?.state),
  district: cleanText(row?.district),
  address: cleanText(row?.address),
  city: cleanText(row?.city),
  sourcePincode: cleanText(row?.source_pincode),
  trustedPincode: cleanText(row?.trusted_pincode),
  millCode: cleanText(row?.mill_code),
  phones: asStringArray(row?.phones),
  emails: asStringArray(row?.emails),
  website: normalizeWebsite(row?.website),
  registeredOffice: cleanText(row?.registered_office),
  operationalStatus: cleanText(row?.operational_status),
  factoryNature: cleanText(row?.factory_nature),
});

const mapManager = (row) => ({
  id: row?.id,
  managerName: cleanText(row?.manager_name),
  designation: cleanText(row?.designation),
  phone: cleanText(row?.phone),
  mobile: cleanText(row?.mobile),
  email: cleanText(row?.email),
});

const compareAlpha = (a, b) =>
  String(a || '').localeCompare(String(b || ''), undefined, { sensitivity: 'base' });

const clampPageSize = (pageSize) => {
  const n = Number(pageSize) || DEFAULT_PAGE_SIZE;
  return Math.min(Math.max(n, 1), MAX_PAGE_SIZE);
};

const tokenize = (search) =>
  String(search || '')
    .trim()
    .split(/\s+/)
    .map((t) => t.trim())
    .filter((t) => t.length > 0);

/** Sanitize search token for PostgREST ilike patterns. */
const sanitizeToken = (token) =>
  String(token)
    .replace(/[,"'\\]/g, ' ')
    .replace(/%/g, '')
    .replace(/_/g, ' ')
    .trim();

/**
 * Build OR filter for factory text fields for one token.
 * Note: match_status intentionally excluded.
 */
const factoryFieldOrFilter = (token) => {
  const t = sanitizeToken(token);
  if (!t) return null;
  const p = `"%${t}%"`;
  return [
    `factory_name.ilike.${p}`,
    `plant_name.ilike.${p}`,
    `state.ilike.${p}`,
    `district.ilike.${p}`,
    `address.ilike.${p}`,
    `city.ilike.${p}`,
    `mill_code.ilike.${p}`,
  ].join(',');
};

/**
 * For multi-token search including manager names:
 * returns { factoryIdsFromManagers, error }
 * Factory IDs that match any token on manager_name/designation.
 */
const findFactoryIdsByManagerTokens = async (tokens) => {
  if (!tokens?.length) return { ids: [], error: null };

  // Collect intersection of factory ids across tokens (AND)
  let intersection = null;

  for (const token of tokens) {
    const t = escapeIlike(token);
    const p = `%${t}%`;
    const { data, error } = await supabase
      ?.from('internal_directory_managers')
      ?.select('factory_id')
      ?.or(`manager_name.ilike.${p},designation.ilike.${p}`);

    if (error) return { ids: [], error };

    const ids = new Set((data || []).map((r) => r.factory_id).filter(Boolean));
    if (intersection === null) {
      intersection = ids;
    } else {
      intersection = new Set([...intersection].filter((id) => ids.has(id)));
    }
    if (intersection.size === 0) break;
  }

  return { ids: [...(intersection || [])], error: null };
};

const applyListFilters = (query, { state, district, operationalStatus, factoryNature }) => {
  let q = query;
  if (state) q = q.eq('state', state);
  if (district) q = q.eq('district', district);
  if (operationalStatus) q = q.eq('operational_status', operationalStatus);
  if (factoryNature) q = q.eq('factory_nature', factoryNature);
  return q;
};

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Directory totals (factories + managers). Read-only.
 */
export const getDirectoryCounts = async () => {
  const [fac, mgr] = await Promise.all([
    supabase.from('internal_directory_factories').select('id', { count: 'exact', head: true }),
    supabase.from('internal_directory_managers').select('id', { count: 'exact', head: true }),
  ]);

  if (fac?.error) return { factories: null, managers: null, error: fac.error };
  if (mgr?.error) return { factories: fac.count ?? 0, managers: null, error: mgr.error };

  return {
    factories: fac.count ?? 0,
    managers: mgr.count ?? 0,
    error: null,
  };
};

/**
 * States with factory counts, sorted A–Z (case-insensitive).
 */
export const getStatesWithCounts = async () => {
  const { data, error } = await supabase
    .from('internal_directory_factories')
    .select('state')
    .not('state', 'is', null);

  if (error) return { data: [], error };

  const counts = new Map();
  (data || []).forEach((row) => {
    const s = cleanText(row?.state);
    if (!s) return;
    counts.set(s, (counts.get(s) || 0) + 1);
  });

  const result = [...counts.entries()]
    .map(([state, factoryCount]) => ({ state, factoryCount }))
    .sort((a, b) => compareAlpha(a.state, b.state));

  return { data: result, error: null };
};

/**
 * Districts for a state with factory counts, sorted A–Z.
 */
export const getDistrictsWithCounts = async (state) => {
  if (!state) return { data: [], error: null };

  const { data, error } = await supabase
    .from('internal_directory_factories')
    .select('district')
    .eq('state', state);

  if (error) return { data: [], error };

  const counts = new Map();
  (data || []).forEach((row) => {
    const d = cleanText(row?.district) || 'Unknown';
    counts.set(d, (counts.get(d) || 0) + 1);
  });

  const result = [...counts.entries()]
    .map(([district, factoryCount]) => ({ district, factoryCount }))
    .sort((a, b) => compareAlpha(a.district, b.district));

  return { data: result, error: null };
};

/**
 * Paginated factory list with filters + search.
 * Does NOT load manager rows — only manager count via embed.
 *
 * @param {object} options
 * @param {string} [options.state]
 * @param {string} [options.district]
 * @param {string} [options.search]
 * @param {string} [options.operationalStatus]
 * @param {string} [options.factoryNature]
 * @param {number} [options.page=1]
 * @param {number} [options.pageSize=50]
 */
export const getFactoriesList = async (options = {}) => {
  const page = Math.max(1, Number(options.page) || 1);
  const pageSize = clampPageSize(options.pageSize);
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  const tokens = tokenize(options.search);
  const filters = {
    state: options.state || null,
    district: options.district || null,
    operationalStatus: options.operationalStatus || null,
    factoryNature: options.factoryNature || null,
  };

  // Base select with manager count (no full manager payload)
  const selectCols = `${FACTORY_LIST_COLUMNS}, internal_directory_managers(count)`;

  // Multi-token AND: each token must match factory fields OR (for manager path) factory id set
  // Strategy: for each token get factory ids matching manager OR use or() on fields.
  // Combined: filter where id in managerIds OR field matches, for each token — expensive.
  // Simpler reliable approach for 831 factories:
  // - If search empty: normal filtered paginated query
  // - If search: resolve matching factory IDs (factory field match ∪ manager match) per token, intersect, then page

  if (tokens.length === 0) {
    let query = supabase
      .from('internal_directory_factories')
      .select(selectCols, { count: 'exact' });

    query = applyListFilters(query, filters);
    query = query.order('factory_name', { ascending: true }).range(from, to);

    const { data, error, count } = await query;
    if (error) return { data: [], total: 0, page, pageSize, error };

    return {
      data: (data || []).map(mapFactoryListRow),
      total: count ?? 0,
      page,
      pageSize,
      error: null,
    };
  }

  // Resolve IDs matching ALL tokens (AND), preferring factory-name path in ranking later
  let candidateIds = null;
  let namePreferred = new Set();

  for (const token of tokens) {
    const t = sanitizeToken(token);
    if (!t) continue;
    const p = `"%${t}%"`;
    const fieldOr = factoryFieldOrFilter(token);
    if (!fieldOr) continue;

    // Factory field matches
    let fq = supabase
      .from('internal_directory_factories')
      .select('id, factory_name')
      .or(fieldOr);
    fq = applyListFilters(fq, filters);

    const { data: facRows, error: facErr } = await fq;
    if (facErr) return { data: [], total: 0, page, pageSize, error: facErr };

    const fieldIds = new Set((facRows || []).map((r) => r.id));
    (facRows || []).forEach((r) => {
      if (r.factory_name && String(r.factory_name).toLowerCase().includes(t.toLowerCase())) {
        namePreferred.add(r.id);
      }
    });

    // Manager matches
    const { data: mgrRows, error: mgrErr } = await supabase
      .from('internal_directory_managers')
      .select('factory_id')
      .or(`manager_name.ilike.${p},designation.ilike.${p}`);

    if (mgrErr) return { data: [], total: 0, page, pageSize, error: mgrErr };

    const mgrFactoryIds = new Set((mgrRows || []).map((r) => r.factory_id).filter(Boolean));

    // If manager hits need filter constraints, validate against factories table
    let managerIdsFiltered = mgrFactoryIds;
    if (filters.state || filters.district || filters.operationalStatus || filters.factoryNature) {
      if (mgrFactoryIds.size > 0) {
        let mq = supabase
          .from('internal_directory_factories')
          .select('id')
          .in('id', [...mgrFactoryIds]);
        mq = applyListFilters(mq, filters);
        const { data: mFac, error: mFacErr } = await mq;
        if (mFacErr) return { data: [], total: 0, page, pageSize, error: mFacErr };
        managerIdsFiltered = new Set((mFac || []).map((r) => r.id));
      } else {
        managerIdsFiltered = new Set();
      }
    }

    const tokenIds = new Set([...fieldIds, ...managerIdsFiltered]);
    if (candidateIds === null) {
      candidateIds = tokenIds;
    } else {
      candidateIds = new Set([...candidateIds].filter((id) => tokenIds.has(id)));
    }

    if (candidateIds.size === 0) {
      return { data: [], total: 0, page, pageSize, error: null };
    }
  }

  const allIds = [...(candidateIds || [])];
  const total = allIds.length;

  // Fetch list rows for all candidates, sort, then page client-side on the id set
  // (831 max — fine; when search is wide we still only fetch matching ids)
  // Chunk .in() if needed (PostgREST URL limits)
  const CHUNK = 100;
  let allRows = [];
  for (let i = 0; i < allIds.length; i += CHUNK) {
    const chunk = allIds.slice(i, i + CHUNK);
    let q = supabase
      .from('internal_directory_factories')
      .select(selectCols)
      .in('id', chunk);
    q = applyListFilters(q, filters);
    const { data, error } = await q;
    if (error) return { data: [], total: 0, page, pageSize, error };
    allRows = allRows.concat(data || []);
  }

  const mapped = allRows.map(mapFactoryListRow);
  mapped.sort((a, b) => {
    const aPref = namePreferred.has(a.id) ? 0 : 1;
    const bPref = namePreferred.has(b.id) ? 0 : 1;
    if (aPref !== bPref) return aPref - bPref;
    return compareAlpha(a.factoryName, b.factoryName);
  });

  const pageRows = mapped.slice(from, to + 1);

  return {
    data: pageRows,
    total,
    page,
    pageSize,
    error: null,
  };
};

/**
 * Factory detail by id. Does not include match_status in returned object.
 */
export const getFactoryDetail = async (factoryId) => {
  if (!factoryId) return { data: null, error: { message: 'Factory id required', status: 400 } };

  const { data, error } = await supabase
    .from('internal_directory_factories')
    .select(FACTORY_DETAIL_COLUMNS)
    .eq('id', factoryId)
    .maybeSingle();

  if (error) return { data: null, error };
  if (!data) return { data: null, error: { message: 'Factory not found', status: 404, code: 'PGRST116' } };

  return { data: mapFactoryDetail(data), error: null };
};

/**
 * Managers for one factory, sorted by name.
 */
export const getManagersForFactory = async (factoryId) => {
  if (!factoryId) return { data: [], error: null };

  const { data, error } = await supabase
    .from('internal_directory_managers')
    .select('id, manager_name, designation, phone, mobile, email')
    .eq('factory_id', factoryId)
    .order('manager_name', { ascending: true });

  if (error) return { data: [], error };

  return {
    data: (data || []).map(mapManager).filter((m) => m.managerName),
    error: null,
  };
};

/**
 * Filter option metadata.
 */
export const getDirectoryFilterOptions = () => ({
  operationalStatus: OPERATIONAL_STATUS_OPTIONS,
  factoryNature: FACTORY_NATURE_OPTIONS,
  defaultPageSize: DEFAULT_PAGE_SIZE,
  maxPageSize: MAX_PAGE_SIZE,
});
